import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DatabaseBackup, Download, Loader2, RefreshCw, Activity } from "lucide-react";
import { Button } from "../ui/button";
import { API_BASE as API } from "../../lib/api";

const fmtSize = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round((n || 0) / 1e3)} KB`);
const fmtAt = (s) => new Date(s).toLocaleString("ar-SA-u-nu-latn", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Riyadh" });

export const PcBackups = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => axios.get(`${API}/platform-console/backups`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy("run");
    try { await axios.post(`${API}/platform-console/backups/run`); toast.success("بدأ النسخ الاحتياطي — يخلص خلال دقيقة تقريباً"); setTimeout(load, 4000); setTimeout(load, 20000); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر البدء"); } finally { setBusy(""); }
  };
  const dl = async (b) => {
    setBusy(b.id);
    try {
      const r = await axios.get(`${API}/platform-console/backups/${b.id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `workzone-backup-${b.at.slice(0, 10)}.jsonl.gz`; a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("تعذّر التحميل"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white p-4 sm:p-6" data-testid="pc-backups">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="flex items-center gap-2 font-heading text-lg font-extrabold"><DatabaseBackup className="h-5 w-5 text-emerald-600" /> النسخ الاحتياطي</p>
            <p className="text-xs text-slate-500">نسخة تلقائية كل ليلة 3:00 فجراً (توقيت الرياض) — نحتفظ بآخر 14 نسخة.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="pc-backups-refresh"><RefreshCw className="h-4 w-4" /></Button>
            <Button size="sm" onClick={run} disabled={busy === "run"} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="pc-backups-run">
              {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />} نسخة الآن
            </Button>
          </div>
        </div>
        {list === null ? <p className="text-sm text-slate-400">جارٍ التحميل…</p> : !list.length ? <p className="text-sm text-slate-400" data-testid="pc-backups-empty">لا توجد نسخ بعد</p> : (
          <div className="divide-y divide-slate-100">
            {list.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`pc-backup-${b.id}`}>
                <div>
                  <p className="font-semibold">{fmtAt(b.at)} <span className="text-xs font-normal text-slate-400">· {b.by === "auto" ? "تلقائي" : b.by}</span></p>
                  <p className="text-xs text-slate-500">
                    {b.status === "ok" ? `${fmtSize(b.size)} · ${b.collections} جدول · ${(b.docs || 0).toLocaleString("en")} سجل` : b.status === "running" ? "قيد التنفيذ…" : <span className="text-rose-600">فشل: {b.error}</span>}
                  </p>
                </div>
                {b.status === "ok" && <Button variant="outline" size="sm" className="gap-1.5" disabled={busy === b.id} onClick={() => dl(b)} data-testid={`pc-backup-dl-${b.id}`}>
                  {busy === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تحميل
                </Button>}
              </div>))}
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-white p-4 sm:p-6" data-testid="pc-uptime">
        <p className="flex items-center gap-2 font-heading text-lg font-extrabold"><Activity className="h-5 w-5 text-blue-600" /> مراقبة توقف الموقع</p>
        <p className="mt-1 text-xs text-slate-500">رابط الفحص (يرجع 200 إذا الموقع وقاعدة البيانات شغالين):</p>
        <code className="mt-1 block rounded-lg bg-slate-100 px-3 py-2 text-xs" dir="ltr" data-testid="pc-health-url">{`${API}/health`}</code>
        <ol className="mt-2 list-decimal space-y-0.5 ps-5 text-xs text-slate-600">
          <li>سجّل مجاناً في uptimerobot.com</li>
          <li>Add New Monitor ← النوع HTTP(s) ← الصق الرابط أعلاه ← كل 5 دقائق</li>
          <li>من Alert Contacts أضف إيميلك أو تطبيق UptimeRobot على جوالك (إشعار فوري إذا طاح الموقع)</li>
        </ol>
        <p className="mt-2 text-[11px] text-slate-400">تنبيهات واتساب تلقائية لرقم مالك المنصة: أخطاء متكررة في الخادم (20 خطأ خلال 10 دقائق) ونفاد رصيد الذكاء الاصطناعي.</p>
      </div>
    </div>
  );
};
